import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";

import { useArrangementState } from "./ArrangementContext";
import { getScreenSequence, getScreenPosition } from "./screenModel";
import type { ScreenDescriptor } from "./screenModel";
import type { ArrangementConfig, ArrangementState } from "./types";

function buildScreenProps(
  descriptor: ScreenDescriptor,
  state: ArrangementState,
  config: ArrangementConfig,
  caseId: string | undefined,
) {
  const { screen_index, total_screens } = getScreenPosition(
    descriptor.screen_name,
    getScreenSequence(state, config),
  );
  return {
    screen_name: descriptor.screen_name,
    step: descriptor.step,
    case_id: caseId,
    is_deceased: state.isDeceased,
    screen_index,
    total_screens,
    ...(descriptor.question_id ? { question_id: descriptor.question_id } : {}),
  };
}

/**
 * Fires `arrangements_screen_viewed` when the active screen changes and exposes
 * `markCompleted()` for `arrangements_screen_completed` on forward navigation.
 * `markCompleted` takes extra properties for a screen whose final answer is
 * worth recording on the completion itself. No-op in read-only (Care
 * Specialist) mode, which renders every sub-view at once and would otherwise
 * emit phantom views.
 */
export function useScreenTracking(descriptor: ScreenDescriptor | null) {
  const { state, config, isReadOnly } = useArrangementState();
  const posthog = usePostHog();
  const { caseId } = useParams<{ caseId: string }>();

  // Keyed on screen_name so a re-entry re-fires but in-place re-renders and the
  // StrictMode double-mount don't. The sequence is only built past this guard,
  // so autosave-driven re-renders cost a cheap early return, not a rebuild.
  const lastViewedRef = useRef<string | null>(null);
  useEffect(() => {
    // Without config the sequence is a stub, and the dedupe below would lock
    // wrong screen_index/total_screens into the view event; the ref is only
    // set on emit, so a capture deferred here still fires once config loads.
    if (isReadOnly || !descriptor || !config) return;
    if (lastViewedRef.current === descriptor.screen_name) return;
    lastViewedRef.current = descriptor.screen_name;
    posthog?.capture(
      "arrangements_screen_viewed",
      buildScreenProps(descriptor, state, config, caseId),
    );
  }, [descriptor, isReadOnly, posthog, state, config, caseId]);

  const markCompleted = (extraProps?: Record<string, unknown>) => {
    if (isReadOnly || !descriptor || !config) return;
    posthog?.capture("arrangements_screen_completed", {
      ...buildScreenProps(descriptor, state, config, caseId),
      ...extraProps,
    });
  };

  return { markCompleted };
}
